"use client"
// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import type { PropsWithChildren } from "react"
import { Navigate, useLocation } from "react-router-dom"
import { useAuth } from "@/hooks/useAuth"
import { useI18n } from "@/i18n"

/**
 * Route guard for the authenticated /app/* area. Unauthenticated visitors
 * are sent back to the public landing route (never shown protected
 * content, even briefly) — the landing route opens the sign-in panel
 * automatically using the redirect location passed in router state.
 */
export function RequireAuth({ children }: PropsWithChildren) {
  const { status } = useAuth()
  const { t } = useI18n()
  const location = useLocation()

  if (status === "loading") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-cream text-ink">
        <p role="status" aria-live="polite">
          {t("state.loading")}
        </p>
      </div>
    )
  }

  if (status === "signedOut") {
    return <Navigate to="/" replace state={{ authRequiredFrom: location.pathname }} />
  }

  return <>{children}</>
}
